import { useEffect, useState } from "react";
import { SystemIds } from "../../services/clienteConexionService.ts";

type ErpType = "onprem" | "cloud";
export type OrigenCredenciales = "guardadas" | "manual";

interface FormularioCredencialesProps {
  origenCredenciales: OrigenCredenciales;
  setOrigenCredenciales: (origen: OrigenCredenciales) => void;
  tieneCredencialesGuardadas: boolean;
  usuarioGuardado: string;

  username: string;
  password: string;
  setUsername: (username: string) => void;
  setPassword: (password: string) => void;
  guardarCredenciales: boolean;
  setGuardarCredenciales: (value: boolean) => void;

  ambiente: string;
  setAmbiente: (ambiente: string) => void;
  dataCenter: string;
  setDataCenter: (dataCenter: string) => void;
  erpType: ErpType | "";
  setErpType: React.Dispatch<React.SetStateAction<ErpType | "">>;

  systemIds: SystemIds;
  setSystemId: (systemId: string) => void;
}

const FormularioCredenciales = ({
  origenCredenciales,
  setOrigenCredenciales,
  tieneCredencialesGuardadas,
  usuarioGuardado,
  username,
  password,
  setUsername,
  setPassword,
  guardarCredenciales,
  setGuardarCredenciales,
  ambiente,
  setAmbiente,
  dataCenter,
  setDataCenter,
  erpType,
  setErpType,
  systemIds,
  setSystemId
}: FormularioCredencialesProps) => {

  const [systemIdOrigen, setSystemIdOrigen] = useState<"dev" | "qas" | "">("");

  // Ambiente = Producción tiene un único System ID posible: el de PRD.
  useEffect(() => {
    if (ambiente === "prd" && systemIds.prd) {
      setSystemId(systemIds.prd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambiente, systemIds.prd]);

  const handleSystemIdOrigenChange = (value: "dev" | "qas") => {
    setSystemIdOrigen(value);
    if (systemIds[value]) setSystemId(systemIds[value]);
  };

  // --- ESTILOS REUTILIZABLES ---
  const inputClass = "w-full bg-[rgb(var(--c-surface))] border border-white/10 rounded-xl p-3 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all placeholder:text-gray-500 mb-1 mt-1";
  const labelClass = "block text-xs font-bold text-blue-400 uppercase tracking-wider mb-2 mt-4 ml-1";

  return (
    <div className="animate-fadeIn">
      <form className="space-y-1">
        {/* ORIGEN DE CREDENCIALES */}
        <label className={labelClass}>Origen de credenciales</label>
        <select
          className={inputClass}
          value={origenCredenciales}
          onChange={(e) => setOrigenCredenciales(e.target.value as OrigenCredenciales)}
        >
          {tieneCredencialesGuardadas && (
            <option value="guardadas" className="bg-[rgb(var(--c-surface))]">Usar credenciales guardadas</option>
          )}
          <option value="manual" className="bg-[rgb(var(--c-surface))]">Ingresar manualmente</option>
        </select>

        {origenCredenciales === "guardadas" ? (
          <p className="text-xs text-gray-400 bg-white/5 border border-white/10 rounded-xl p-3 mt-1">
            Se usará el PUser guardado para este cliente{usuarioGuardado ? ` (${usuarioGuardado})` : ''}.
          </p>
        ) : (
          <div className="animate-slideInRight space-y-1">
            <label className={labelClass}>Usuario 'Puser - CIG'</label>
            <input
              type="text"
              className={inputClass}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Ej: P000000"
              required
            />

            <label className={labelClass}>Contraseña 'Puser - CIG'</label>
            <input
              type="password"
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />

            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer mt-2 ml-1">
              <input
                type="checkbox"
                checked={guardarCredenciales}
                onChange={(e) => setGuardarCredenciales(e.target.checked)}
                className="w-4 h-4 rounded border-white/10 bg-zinc-800 accent-blue-500"
              />
              Guardar estas credenciales para este cliente
            </label>
          </div>
        )}

        {/* AMBIENTE */}
        <label className={labelClass}>Ambiente</label>
        <select
          className={`${inputClass} ${ambiente === 'prd' ? 'text-red-400 font-bold' : 'text-white'}`}
          value={ambiente}
          onChange={(e) => setAmbiente(e.target.value)}
          required
        >
          <option value="" className="bg-[rgb(var(--c-surface))]">Selecciona una opción</option>
          <option value="test" className="bg-[rgb(var(--c-surface))]">🧪 Test / QA</option>
          <option value="prd" className="bg-[rgb(var(--c-surface))]">🚀 Producción</option>
        </select>

        {ambiente === "test" && (systemIds.dev || systemIds.qas) && (
          <div className="animate-slideInRight">
            <label className={labelClass}>¿Qué System ID quieres usar?</label>
            <select
              className={inputClass}
              value={systemIdOrigen}
              onChange={(e) => handleSystemIdOrigenChange(e.target.value as "dev" | "qas")}
            >
              <option value="" className="bg-[rgb(var(--c-surface))]">Selecciona una opción</option>
              {systemIds.dev && <option value="dev" className="bg-[rgb(var(--c-surface))]">DEV ({systemIds.dev})</option>}
              {systemIds.qas && <option value="qas" className="bg-[rgb(var(--c-surface))]">QAS ({systemIds.qas})</option>}
            </select>
          </div>
        )}

        {/* ERP Y DATACENTER EN GRID */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Tipo de ERP</label>
            <select
              className={inputClass}
              value={erpType}
              onChange={(e) => setErpType(e.target.value as ErpType | "")}
            >
              <option value="" className="bg-[rgb(var(--c-surface))]">ERP</option>
              <option value="onprem" className="bg-[rgb(var(--c-surface))]">On-Premise</option>
              <option value="cloud" className="bg-[rgb(var(--c-surface))]">Cloud</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Data Center</label>
            <select
              className={inputClass}
              value={dataCenter}
              onChange={(e) => setDataCenter(e.target.value)}
              required
            >
              <option value="" className="bg-[rgb(var(--c-surface))]">DC</option>
              <option value="eu" className="bg-[rgb(var(--c-surface))]">Europa</option>
              <option value="us" className="bg-[rgb(var(--c-surface))]">USA</option>
            </select>
          </div>
        </div>
      </form>
    </div>
  );
};

export default FormularioCredenciales;
